import CustomTitleBar from 'src/components/GuideMeTitleBar';
import Button from 'src/components/design-system/Button';
import { CanClosePeriodPermissions } from 'src/constants/permissionsList';
import usePermission from 'src/hooks/usePermissions';
import useViewCalendarYear from 'src/hooks/useViewCalendarYear';

const CalendarYearViewPeriodsContent = () => (
  <ul className="px-4 font-bold text-[13px]">
    <li className="list-disc font-semibold">
      Calendar periods are automatically managed by the system; no maintenance is required.
    </li>
    <li className="list-disc font-semibold">
      Click
      <Button color="secondary" className="inline-block" readonly={true} size="lg">
        View Periods
      </Button>
      to view Accounting Periods for selected Fiscal Year.
    </li>
    <li className="list-disc font-semibold">
      Expand Previous Fiscal Years section to view past years and their periods.
    </li>
  </ul>
);
const ViewEditPeriodsContent = ({
  hasCanClosePeriodPermission,
}: {
  hasCanClosePeriodPermission: boolean;
}) => (
  <ul className="px-4 font-bold text-[13px]">
    <li className="list-disc font-semibold">
      Calendar periods are automatically managed by the system; no maintenance is required.
    </li>
    <li className="list-disc font-semibold">
      Click
      <Button className="inline-block" size="lg" readonly={true}>
        Create New Year
      </Button>
      to create a Fiscal Year.
    </li>
    <li className="list-disc font-semibold">
      Click
      <Button color="secondary" className="inline-block" size="lg" readonly={true}>
        View Periods
      </Button>
      to view Accounting Periods for selected Fiscal Year.
    </li>
    <li className="list-disc font-semibold">
      Click
      <Button color="error" className="inline-block" size="lg" readonly={true}>
        Delete
      </Button>
      to delete the last Fiscal Year.
    </li>
    <li className="list-disc font-semibold">
      Expand the Previous Fiscal Years section to view past years and their periods.
    </li>
    {hasCanClosePeriodPermission ? (
      <li className="list-disc font-semibold">
        Periods must be closed in chronological order up to the current period. Future periods
        cannot be closed. Budget and Forecast $ amounts cannot be updated for closed periods. Users
        will not be allowed to create or approve POs and Invoices that contain closed periods. After
        a period is closed, it cannot be opened.
      </li>
    ) : (
      <li className="list-disc font-semibold">
        Budget and Forecast $ amounts cannot be updated for closed periods. Users will not be
        allowed to create or approve POs and Invoices that contain closed periods.
      </li>
    )}
  </ul>
);
const NonCalendarViewPeriodsContent = () => (
  <ul className="px-4 font-bold text-[13px]">
    <li className="list-disc font-semibold">
      Click
      <Button color="secondary" className="inline-block" size="lg" readonly={true}>
        View Periods
      </Button>
      to view Accounting Periods for selected Fiscal Year.
    </li>
    <li className="list-disc font-semibold">
      Expand the Previous Fiscal Years section to view past years and their periods.
    </li>
  </ul>
);
const NonCalendarViewEditPeriodsContentPg1 = ({
  hasCanClosePeriodPermission,
}: {
  hasCanClosePeriodPermission: boolean;
}) => (
  <ul className="px-4 font-bold text-[13px]">
    <li className="list-disc font-semibold">
      Click
      <Button className="inline-block" size="lg" readonly={true}>
        Create New Year
      </Button>
      to create a Fiscal Year.
    </li>

    <li className="list-disc font-semibold">
      Expand the Previous Fiscal Years section to view past years and their periods.
    </li>
    <li className="list-disc font-semibold">
      Click
      <Button color="error" className="inline-block" size="lg" readonly={true}>
        Delete
      </Button>
      to delete the last Fiscal Year.
    </li>
    {hasCanClosePeriodPermission ? (
      <li className="list-disc font-semibold">
        Periods must be closed in chronological order up to the current period. Future periods
        cannot be closed. Budget and Forecast $ amounts cannot be updated for closed periods. Users
        will not be allowed to create or approve POs and Invoices that contain closed periods. After
        a period is closed, it cannot be opened.
      </li>
    ) : (
      <li className="list-disc font-semibold">
        Budget and Forecast $ amounts cannot be updated for closed periods. Users will not be
        allowed to create or approve POs and Invoices that contain closed periods.
      </li>
    )}
  </ul>
);
const NonCalendarViewEditPeriodsContentPg2 = () => (
  <ul className="px-4 font-bold text-[13px]">
    <li className="list-disc font-semibold">
      Click
      <Button color="secondary" className="inline-block" size="lg" readonly={true}>
        View Periods
      </Button>
      to view Accounting Periods for selected Fiscal Year.
    </li>
    <li className="list-disc font-semibold">
      Click
      <Button className="inline-block" readonly={true}>
        Add Period
      </Button>
      to add a new Accounting Period for the selected Fiscal year.
      <ul className="pl-8 list-disc font-semibold">
        <li>
          A New Accounting Period will be added before the last Accounting Period in a Fiscal Year.
        </li>
        <li>The End date for a newly added Accounting Period will be a manual entry.</li>
        <li>
          The Start date for the last Accounting Period will be set by the system as the day that
          comes after End Date of the newly created Accounting Period.
        </li>
        <li>
          Click
          <Button color="error" className="inline-block" size="lg" readonly={true}>
            Delete
          </Button>
          to delete an Accounting Period
        </li>
      </ul>
    </li>
  </ul>
);
const NonCalendarViewEditPeriodsContentPg3 = () => (
  <ul className="px-4 font-bold text-[13px]">
    <li className="list-disc font-semibold">
      Click
      <Button className="inline-block" size="lg" color="info" readonly={true}>
        Edit
      </Button>
      to modify End dates for Accounting Periods in a Fiscal Year.
      <ul className="pl-8 list-disc font-semibold">
        <li>Only future Accounting Periods are editable.</li>
        <li>
          The End Date of the last Accounting Period is read only when there is a Fiscal Year after
          that year.
        </li>
        <li>
          Click
          <Button color="error" className="inline-block" size="lg" readonly={true}>
            Delete
          </Button>
          to delete an Accounting Period
        </li>
      </ul>
    </li>
    <li className="list-disc font-semibold">
      Click
      <Button size="md" color="info" readonly={true}>
        Close Period
      </Button>
      to close the period.
      <ul className="pl-8 list-disc font-semibold">
        <li>Current or past periods can be closed, future periods cannot be closed.</li>
      </ul>
    </li>
  </ul>
);

export const useGuideMeSteps = () => {
  const { isCalendarYear, hasEditGLPeriodsPermission, pageTitle, isGlPeriodsLoading } =
    useViewCalendarYear();

  const hasCanClosePeriodPermission = usePermission({ permissions: CanClosePeriodPermissions });

  return isGlPeriodsLoading
    ? []
    : [
        ...(isCalendarYear && !hasEditGLPeriodsPermission
          ? [
              {
                title: (
                  <CustomTitleBar title={pageTitle} className="font-bold text-xl text-red-600" />
                ),
                content: <CalendarYearViewPeriodsContent />,
              },
            ]
          : []),
        ...(isCalendarYear && hasEditGLPeriodsPermission
          ? [
              {
                title: (
                  <CustomTitleBar title={pageTitle} className="font-bold text-xl text-red-600" />
                ),
                content: (
                  <ViewEditPeriodsContent
                    hasCanClosePeriodPermission={hasCanClosePeriodPermission}
                  />
                ),
              },
            ]
          : []),
        ...(!isCalendarYear && !hasEditGLPeriodsPermission
          ? [
              {
                title: (
                  <CustomTitleBar title={pageTitle} className="font-bold text-xl text-red-600" />
                ),
                content: <NonCalendarViewPeriodsContent />,
              },
            ]
          : []),
        ...(!isCalendarYear && hasEditGLPeriodsPermission
          ? [
              {
                title: (
                  <CustomTitleBar
                    title={'Create/View Fiscal Year(s)'}
                    className="font-bold text-xl text-red-600"
                  />
                ),
                content: (
                  <NonCalendarViewEditPeriodsContentPg1
                    hasCanClosePeriodPermission={hasCanClosePeriodPermission}
                  />
                ),
              },
              {
                title: (
                  <CustomTitleBar
                    title={'View/Add Periods'}
                    className="font-bold text-xl text-red-600"
                  />
                ),
                content: <NonCalendarViewEditPeriodsContentPg2 />,
              },
              {
                title: (
                  <CustomTitleBar
                    title={'Edit Periods'}
                    className="font-bold text-xl text-red-600"
                  />
                ),
                content: <NonCalendarViewEditPeriodsContentPg3 />,
              },
            ]
          : []),
      ];
};
